// Live credit-service client (gateway → /api/v1/credit/*). Same auth pattern as
// the payroll client: the httpOnly session cookie (attachCookieAuth) and never
// nuke the session on error, so a credit failure degrades to an empty state.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';
import type {
  ApplyInput,
  CreditAgreement,
  CreditApplication,
  CreditFacility,
  CreditInterestMethod,
  CreditOffer,
  CreditProduct,
  CreditProgram,
  CreditTerms,
  Currency,
  SaveProgramInput,
  ScheduleLine,
} from '../types';

const BASE = '/api/v1/credit';

/** A partner-product agreement as the employer sees it (approve/decline surface). */
export interface TenantAgreement {
  id: string;
  productId: string;
  productName: string;
  partnerId: string;
  status: string; // pending | employer_review | active | declined | revoked | suspended
  category: string;
  requiresEmployerApproval: boolean;
  createdAt: string;
}

/** Per-tenant employee-visibility toggles (the employer's key of the two-key gate). */
export interface CreditModuleSettings {
  showEmployeeMarketplace: boolean; // employee "My Products"
  showEmployeeLoans: boolean; // employee "Loans & Advances"
}
const num = (v: any): number => (v == null ? 0 : Number(v));
const method = (m: any): CreditInterestMethod => (m === 'reducing' ? 'reducing' : 'flat');

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

const get = async <T = any>(url: string): Promise<T> => (await http.get<T>(url)).data;
const post = async <T = any>(url: string, body?: any): Promise<T> => (await http.post<T>(url, body ?? {})).data;
const put = async <T = any>(url: string, body?: any): Promise<T> => (await http.put<T>(url, body ?? {})).data;

// --- mappers (snake_case API → camelCase UI) ---

function mapOffer(o: any): CreditOffer {
  return {
    productId: o.product_id,
    code: o.code ?? '',
    name: o.name ?? 'Loan',
    currency: (o.currency as Currency) ?? 'TZS',
    interestMethod: method(o.interest_method),
    annualRate: num(o.annual_rate),
    minAmount: num(o.min_amount),
    maxAmount: num(o.max_amount),
    maxTermMonths: Number(o.max_term_months ?? 0),
    fundingModel: o.funding_model ?? 'employer_funded',
    partnerId: o.partner_id ?? null,
    category: o.category ?? 'CREDIT',
    fulfillmentDomain: o.fulfillment_domain ?? 'credit',
    fulfillmentReference: o.fulfillment_reference ?? null,
    planType: o.plan_type ?? undefined,
    coverage: o.coverage ?? undefined,
    employeePremium: o.employee_premium != null ? num(o.employee_premium) : undefined,
    employerContribution: o.employer_contribution != null ? num(o.employer_contribution) : undefined,
  };
}

function mapApplication(a: any): CreditApplication {
  return {
    id: a.id,
    productId: a.product_id,
    employeeRef: a.employee_ref,
    requestedAmount: num(a.requested_amount),
    requestedTermMonths: Number(a.requested_term_months ?? 0),
    purpose: a.purpose || undefined,
    status: a.status,
    decisionReason: a.decision_reason || undefined,
    createdAt: a.created_at ?? '',
  };
}

function mapTerms(t: any): CreditTerms {
  const raw = typeof t === 'string' ? safeParse(t) : t ?? {};
  return {
    principal: num(raw.principal),
    currency: (raw.currency as Currency) ?? 'TZS',
    interestMethod: method(raw.interest_method),
    annualRate: num(raw.annual_rate),
    installment: num(raw.installment),
    termMonths: Number(raw.term_months ?? 0),
    totalRepayable: num(raw.total_repayable),
    fundingModel: raw.funding_model || undefined,
  };
}

function safeParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
}

function mapAgreement(g: any): CreditAgreement {
  return {
    id: g.id,
    applicationId: g.application_id,
    status: g.status ?? 'pending',
    terms: mapTerms(g.terms),
    createdAt: g.created_at ?? '',
  };
}

function mapFacility(f: any): CreditFacility {
  return {
    id: f.id,
    applicationId: f.application_id,
    productId: f.product_id,
    employeeRef: f.employee_ref,
    currency: (f.currency as Currency) ?? 'TZS',
    principal: num(f.principal),
    interestMethod: method(f.interest_method),
    annualRate: num(f.annual_rate),
    totalRepayable: num(f.total_repayable),
    outstandingBalance: num(f.outstanding_balance),
    installment: num(f.installment),
    termMonths: Number(f.term_months ?? 0),
    status: f.status,
    fundingModel: f.funding_model ?? 'employer_funded',
    partnerId: f.partner_id ?? null,
    disbursedAt: f.disbursed_at || undefined,
    createdAt: f.created_at ?? '',
  };
}

function mapScheduleLine(s: any): ScheduleLine {
  return {
    seq: Number(s.seq ?? 0),
    duePeriod: s.due_period ?? '',
    amountDue: num(s.amount_due),
    principalComponent: num(s.principal_component),
    interestComponent: num(s.interest_component),
    status: s.status ?? 'pending',
    amountPaid: num(s.amount_paid),
  };
}

function mapProduct(p: any): CreditProduct {
  return {
    id: p.id,
    code: p.code ?? '',
    name: p.name ?? 'Loan',
    fundingModel: p.funding_model ?? 'employer_funded',
    productStatus: p.product_status ?? 'active',
    currency: (p.currency as Currency) ?? 'TZS',
    interestMethod: method(p.interest_method),
    defaultAnnualRate: num(p.default_annual_rate),
    maxAnnualRate: num(p.max_annual_rate),
    minAmount: num(p.min_amount),
    maxAmount: num(p.max_amount),
    maxTermMonths: Number(p.max_term_months ?? 0),
    category: p.category ?? 'CREDIT',
    fulfillmentDomain: p.fulfillment_domain ?? 'credit',
  };
}

function mapProgram(p: any): CreditProgram {
  return {
    productId: p.product_id,
    enabled: !!p.enabled,
    approvalWorkflow: p.approval_workflow ?? 'single',
    annualRateOverride: p.annual_rate_override ?? null,
    maxAmountOverride: p.max_amount_override ?? null,
    maxTermOverride: p.max_term_override ?? null,
  };
}

export interface EmployerAnalytics {
  participants: number;
  active_facilities: number;
  by_category: Record<string, number>;
  on_time_rate: number;
  at_risk: number;
  band_distribution: Record<string, number>;
  interest_saved: number;
}

export interface FinancialHealth {
  total_repaid: number;
  interest_paid: number;
  interest_saved: number;
  active_debt: number;
  monthly_deductions: number;
  products_completed: number;
  currency: string;
}

export interface PrepayQuote {
  mode: string;
  amount: number;
  interest_saved: number;
  new_remaining_term: number;
  new_installment: number;
  remaining_after: number;
}
export interface PayoffQuote {
  allowed: boolean;
  outstanding: number;
  waived_interest: number;
  fee: number;
  payoff_amount: number;
}

export interface MarketplaceOfferView extends CreditOffer {
  partnerName: string | null;
  reasons: string[];
  active: boolean;
  /** null = no band rule on this product. */
  eligible: boolean | null;
  eligibilityReason: string | null;
  /** Rate after the caller's band discount (only when lower than list). */
  effectiveRate: number | null;
}
export interface MyMarketplace {
  categories: { key: string; offers: MarketplaceOfferView[] }[];
  recommended: MarketplaceOfferView[];
  featured?: { campaign_id: string; title: string; message?: string; sponsored: boolean; offer: MarketplaceOfferView } | null;
}

export interface ScoreFactor {
  key: string;
  points: number;
  max: number;
  rating: 'excellent' | 'good' | 'building' | string;
  detail: string;
}
export interface KaziScore {
  score: number;
  band: string;
  factors: ScoreFactor[];
  suggestions: string[];
  computed_at: string;
}
export interface ScoreFeedEntry {
  delta: number;
  score_after: number;
  reason_key: string;
  detail: string;
  occurred_at: string;
}

export interface SettlementConfirmation {
  id: string;
  settlement_id: string;
  partner_name?: string;
  period_start: string;
  period_end: string;
  collected: number;
  employees: number;
  status: 'pending' | 'confirmed' | 'disputed';
  note?: string | null;
  decided_at?: string | null;
}

export const creditApi = {
  // --- employee self-service ---
  async myOffers(): Promise<CreditOffer[]> {
    const d = await get<{ offers: any[] }>(`${BASE}/me/offers`);
    return (d.offers ?? []).map(mapOffer);
  },
  /** Loan document trail (employee side). */
  async applicationDocuments(appId: string): Promise<{ documents: { id: string; doc_key: string; label: string; file_name: string; size_bytes: number; uploaded_at: string }[]; required: { key: string; label: string }[]; states?: Record<string, { status: string; note?: string }> }> {
    return get(`${BASE}/me/applications/${appId}/documents`);
  },
  async uploadApplicationDocument(appId: string, file: File, docKey: string, label: string): Promise<void> {
    await http.post(`${BASE}/me/applications/${appId}/documents?doc_key=${encodeURIComponent(docKey)}&label=${encodeURIComponent(label)}&file_name=${encodeURIComponent(file.name)}`,
      file, { headers: { 'Content-Type': file.type } });
  },
  async downloadApplicationDocument(appId: string, docId: string, filename: string): Promise<void> {
    const res = await http.get(`${BASE}/me/applications/${appId}/documents/${docId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  /** KaziPeople Sign S3 — the company stamp (auto-applied on employer countersign). */
  async listStamps(): Promise<{ id: string; name: string; stamp_type: string; status: string; created_by: string; created_at: string }[]> {
    const res = await get<{ stamps: any[] }>(`${BASE}/stamps`);
    return res.stamps ?? [];
  },
  async registerStamp(name: string, pngBase64: string): Promise<void> {
    await http.post(`${BASE}/stamps`, { name, stamp_type: 'company', png_base64: pngBase64 });
  },
  async approveStamp(id: string): Promise<void> {
    await http.post(`${BASE}/stamps/${id}/approve`, {});
  },
  async revokeStamp(id: string): Promise<void> {
    await http.post(`${BASE}/stamps/${id}/revoke`, {});
  },

  /** Signing v2 — the employer's countersign seat on new loan agreements. */
  async countersignPending(): Promise<{ agreement_id: string; borrower_name: string; product_name: string; principal: number; currency: string; requested_at: string }[]> {
    const res = await get<{ pending: any[] }>(`${BASE}/agreements/countersign-pending`);
    return res.pending ?? [];
  },
  async countersignAgreement(agreementId: string, mark?: { signature_png?: string; typed_name?: string }): Promise<void> {
    await http.post(`${BASE}/agreements/${agreementId}/countersign`, mark ?? {});
  },
  async declineAgreementSigning(agreementId: string, note: string): Promise<void> {
    await http.post(`${BASE}/agreements/${agreementId}/decline`, { note });
  },
  async agreementSigning(agreementId: string): Promise<{ status: string; signers: { signer_order: number; signer_role: string; signer_name: string; status: string; signed_at?: string }[] } | null> {
    try { return await get(`${BASE}/agreements/${agreementId}/signing`); } catch { return null; }
  },
  async downloadSigningCertificate(agreementId: string): Promise<void> {
    const res = await http.get(`${BASE}/agreements/${agreementId}/certificate.pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = 'signing-certificate.pdf'; a.click();
    URL.revokeObjectURL(url);
  },

  /** Partner settlement confirmations (employer finance handshake). */
  async settlementConfirmations(): Promise<SettlementConfirmation[]> {
    const d = await get<{ confirmations: SettlementConfirmation[] }>(`${BASE}/settlement-confirmations`);
    return d.confirmations ?? [];
  },
  async settlementConfirmationLines(id: string): Promise<{ employee_name: string; product_name: string; collected: number }[]> {
    const d = await get<any>(`${BASE}/settlement-confirmations/${id}/lines`);
    return d.lines ?? [];
  },
  async decideSettlementConfirmation(id: string, decision: 'confirm' | 'dispute', note?: string): Promise<SettlementConfirmation> {
    return post<SettlementConfirmation>(`${BASE}/settlement-confirmations/${id}/${decision}`, note ? { note } : {});
  },
  async reopenSettlementConfirmation(id: string): Promise<SettlementConfirmation> {
    return post<SettlementConfirmation>(`${BASE}/settlement-confirmations/${id}/reopen`);
  },

  /** Employer marketplace insights (k-anonymous aggregates). */
  async employerAnalytics(): Promise<EmployerAnalytics> {
    return get<EmployerAnalytics>(`${BASE}/analytics/employer`);
  },
  /** Employee financial health (design §14). */
  async financialHealth(): Promise<FinancialHealth> {
    return get<FinancialHealth>(`${BASE}/me/financial-health`);
  },
  /** Campaign-nudge opt-out (Trust Center "Product offers" toggle). */
  async marketingPrefs(): Promise<{ campaign_offers: boolean }> {
    return get<{ campaign_offers: boolean }>(`${BASE}/me/marketing-prefs`);
  },
  async setMarketingPrefs(campaignOffers: boolean): Promise<{ campaign_offers: boolean }> {
    return put<{ campaign_offers: boolean }>(`${BASE}/me/marketing-prefs`, { campaign_offers: campaignOffers });
  },
  /** Early repayment (M4): quote an extra payment's effect. */
  async prepayQuote(facilityId: string, amount: number, mode: string): Promise<PrepayQuote> {
    return get<PrepayQuote>(`${BASE}/me/facilities/${facilityId}/prepay-quote?amount=${amount}&mode=${mode}`);
  },
  async payoffQuote(facilityId: string): Promise<PayoffQuote> {
    return get<PayoffQuote>(`${BASE}/me/facilities/${facilityId}/payoff-quote`);
  },
  /** Early repayment (M4): push a mobile-money payment prompt. */
  async payFacility(facilityId: string, input: { amount?: number; mode: string; phone: string; network: string; bank_otp?: string }): Promise<{ status: string; message?: string }> {
    return post<any>(`${BASE}/me/facilities/${facilityId}/pay`, input);
  },
  /** KaziScore (M2, employee-only shadow). */
  async myScore(): Promise<KaziScore> {
    return get<KaziScore>(`${BASE}/me/score/`);
  },
  async myScoreHistory(): Promise<ScoreFeedEntry[]> {
    const d = await get<{ entries: ScoreFeedEntry[] }>(`${BASE}/me/score/history`);
    return d.entries ?? [];
  },
  /** M1 discovery: categorized, cost-ranked, reason-annotated offers + recommendations. */
  async myMarketplace(q?: string): Promise<MyMarketplace> {
    const d = await get<any>(`${BASE}/me/marketplace${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    const mapMo = (o: any): MarketplaceOfferView => ({
      ...mapOffer(o),
      partnerName: o.partner_name || null,
      reasons: Array.isArray(o.reasons) ? o.reasons : [],
      active: !!o.active,
      eligible: typeof o.eligible === 'boolean' ? o.eligible : null,
      eligibilityReason: o.eligibility_reason || null,
      effectiveRate: typeof o.effective_rate === 'number' && o.effective_rate > 0 ? o.effective_rate : null,
    });
    return {
      categories: (d.categories ?? []).map((c: any) => ({ key: c.key, offers: (c.offers ?? []).map(mapMo) })),
      recommended: (d.recommended ?? []).map(mapMo),
      featured: d.featured
        ? { campaign_id: d.featured.campaign_id, title: d.featured.title, message: d.featured.message, sponsored: true, offer: mapMo(d.featured.offer) }
        : null,
    };
  },
  /** First-time marketplace policy acknowledgement. */
  async getMarketplaceConsent(): Promise<{ acknowledged: boolean; policy_version: string }> {
    return get<{ acknowledged: boolean; policy_version: string }>(`${BASE}/me/marketplace-consent`);
  },
  async acknowledgeMarketplaceConsent(): Promise<{ acknowledged: boolean; policy_version: string }> {
    return post<{ acknowledged: boolean; policy_version: string }>(`${BASE}/me/marketplace-consent`, {});
  },
  async myApplications(): Promise<CreditApplication[]> {
    const d = await get<{ applications: any[] }>(`${BASE}/me/applications`);
    return (d.applications ?? []).map(mapApplication);
  },
  async myAgreements(): Promise<CreditAgreement[]> {
    const d = await get<{ agreements: any[] }>(`${BASE}/me/agreements`);
    return (d.agreements ?? []).map(mapAgreement);
  },
  async myFacilities(): Promise<CreditFacility[]> {
    const d = await get<{ facilities: any[] }>(`${BASE}/me/facilities`);
    return (d.facilities ?? []).map(mapFacility);
  },
  /** Repayment schedule for one of MY facilities (ownership enforced server-side). */
  async mySchedule(facilityId: string): Promise<ScheduleLine[]> {
    const d = await get<{ schedule: any[] }>(`${BASE}/me/facilities/${facilityId}/schedule`);
    return (d.schedule ?? []).map(mapScheduleLine);
  },
  async apply(input: ApplyInput): Promise<CreditApplication> {
    const a = await post<any>(`${BASE}/me/applications`, {
      product_id: input.productId,
      amount: input.amount,
      term_months: input.termMonths,
      purpose: input.purpose ?? '',
    });
    return mapApplication(a);
  },
  async requestAcceptOtp(agreementId: string): Promise<void> {
    await http.post(`${BASE}/me/agreements/${agreementId}/accept-otp`, {});
  },
  async acceptAgreement(agreementId: string, signing?: { signature_png?: string; typed_name?: string; otp_code?: string }): Promise<CreditFacility> {
    return mapFacility(await post<any>(`${BASE}/me/agreements/${agreementId}/accept`, signing ?? {}));
  },

  // --- HR / Finance ---
  async listApplications(status?: string): Promise<CreditApplication[]> {
    const d = await get<{ applications: any[] }>(`${BASE}/applications${status ? `?status=${status}` : ''}`);
    return (d.applications ?? []).map(mapApplication);
  },
  async approveApplication(id: string): Promise<CreditAgreement> {
    return mapAgreement(await post<any>(`${BASE}/applications/${id}/approve`));
  },
  async rejectApplication(id: string, reason?: string): Promise<void> {
    await post(`${BASE}/applications/${id}/reject`, reason ? { reason } : {});
  },
  async listFacilities(): Promise<CreditFacility[]> {
    const d = await get<{ facilities: any[] }>(`${BASE}/facilities`);
    return (d.facilities ?? []).map(mapFacility);
  },
  async listCatalogue(): Promise<CreditProduct[]> {
    const d = await get<{ products: any[] }>(`${BASE}/products`);
    return (d.products ?? []).map(mapProduct);
  },
  async listPrograms(): Promise<CreditProgram[]> {
    const d = await get<{ programs: any[] }>(`${BASE}/programs`);
    return (d.programs ?? []).map(mapProgram);
  },
  async saveProgram(productId: string, body: SaveProgramInput): Promise<CreditProgram> {
    const p = await put<any>(`${BASE}/programs/${productId}`, {
      enabled: body.enabled,
      approval_workflow: body.approvalWorkflow ?? 'single',
      annual_rate_override: body.annualRateOverride ?? null,
      max_amount_override: body.maxAmountOverride ?? null,
      max_term_override: body.maxTermOverride ?? null,
    });
    return mapProgram(p);
  },
  /** Clean marketplace on/off — flips employee visibility without touching rate/limit config. */
  async setProgramEnabled(productId: string, enabled: boolean): Promise<CreditProgram> {
    return mapProgram(await put<any>(`${BASE}/programs/${productId}/enabled`, { enabled }));
  },
  /** Which employee-facing financial surfaces this user should see: My Products
   *  (employer marketplace toggle ∧ substance) and Loans & Advances (employer toggle). */
  async productsVisible(): Promise<{ visible: boolean; loansVisible: boolean }> {
    const d = await get<{ visible: boolean; loans_visible?: boolean }>(`${BASE}/me/products-visible`);
    return { visible: !!d.visible, loansVisible: d.loans_visible !== false };
  },
  /** Per-tenant employee-visibility toggles (admin, Company Settings card). */
  async getModuleSettings(): Promise<CreditModuleSettings> {
    const d = await get<any>(`${BASE}/settings`);
    return {
      showEmployeeMarketplace: d.show_employee_marketplace !== false,
      showEmployeeLoans: d.show_employee_loans !== false,
    };
  },
  async updateModuleSettings(input: Partial<CreditModuleSettings>): Promise<CreditModuleSettings> {
    const d = await put<any>(`${BASE}/settings`, {
      ...(input.showEmployeeMarketplace != null ? { show_employee_marketplace: input.showEmployeeMarketplace } : {}),
      ...(input.showEmployeeLoans != null ? { show_employee_loans: input.showEmployeeLoans } : {}),
    });
    return {
      showEmployeeMarketplace: d.show_employee_marketplace !== false,
      showEmployeeLoans: d.show_employee_loans !== false,
    };
  },

  // --- Employer marketplace (discover + request published partner products) ---
  async marketplaceProducts(): Promise<MarketplaceProduct[]> {
    const d = await get<{ products: any[] }>(`${BASE}/marketplace/products`);
    return (d.products ?? []).map(mapMarketplaceProduct);
  },
  async requestAccess(productId: string): Promise<void> {
    await post(`${BASE}/marketplace/products/${productId}/request-access`);
  },

  // --- Partner-level approval gate (the employer approves a PARTNER + consents to data-sharing
  //     BEFORE enabling any of that partner's products) ---
  /** This employer's standing with a partner (none|pending|approved|suspended). */
  async partnerRelationship(partnerId: string): Promise<PartnerRelationship> {
    const r = await get<any>(`${BASE}/partners/${partnerId}/relationship`);
    return {
      partnerId: r.partner_id,
      status: r.status ?? 'none',
      approvedAt: r.approved_at ?? null,
      consentAt: r.consent_at ?? null,
    };
  },
  /** Approve a partner (organization-level) and record data-sharing consent. Consent is mandatory. */
  async approvePartner(partnerId: string, consentScope?: Record<string, unknown>): Promise<PartnerRelationship> {
    const r = await post<any>(`${BASE}/partners/${partnerId}/approve`, {
      consent: true,
      ...(consentScope ? { consent_scope: consentScope } : {}),
    });
    return {
      partnerId: r.partner_id,
      status: r.status ?? 'approved',
      approvedAt: r.approved_at ?? null,
      consentAt: r.consent_at ?? null,
    };
  },
  /** Partner-product agreements for this tenant (the employer sign surface). */
  async listTenantAgreements(status?: string): Promise<TenantAgreement[]> {
    const d = await get<{ agreements: any[] }>(`${BASE}/agreements${status ? `?status=${status}` : ''}`);
    return (d.agreements ?? []).map((a) => ({
      id: a.id,
      productId: a.product_id,
      productName: a.product_name ?? '',
      partnerId: a.partner_id,
      status: a.status ?? '',
      category: a.category ?? '',
      requiresEmployerApproval: !!a.requires_employer_approval,
      createdAt: a.created_at ?? '',
    }));
  },
  /** The employer approves a partner offer — the product becomes enabled for the company. */
  async approveTenantAgreement(id: string): Promise<void> {
    await post(`${BASE}/agreements/${id}/approve`);
  },
  async declineTenantAgreement(id: string, reason?: string): Promise<void> {
    await post(`${BASE}/agreements/${id}/decline`, reason ? { reason } : {});
  },

  // Record data-sharing consent (compliance-service) before applying for a partner
  // product — the partner only receives the employee's data after this grant.
  // documentVersion = the published data_sharing_consent document the user agreed to.
  async grantDataSharing(partnerId: string, productId: string, documentVersion?: number): Promise<void> {
    await post('/api/v1/compliance/data-sharing-grants', {
      partner_id: partnerId,
      product_id: productId,
      purpose: 'loan_underwriting',
      ...(documentVersion ? { document_version: documentVersion } : {}),
    });
  },

  // Download the signed loan-agreement PDF (tamper-evident record) for an accepted
  // agreement. Fetched with the auth token, then saved via a blob URL.
  async downloadAgreement(agreementId: string): Promise<void> {
    const resp = await http.get(`${BASE}/me/agreements/${agreementId}/document`, { responseType: 'blob' });
    const url = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'loan-agreement.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// A published partner product as the employer discovers it, tagged with this tenant's
// agreement status (null = not yet requested).
export type MarketplaceProduct = CreditProduct & {
  partnerId: string;
  partnerName: string;
  agreementStatus: string | null;
};

// The employer's organization-level standing with a partner. 'none' = never approved; the
// partner-level gate requires 'approved' before any of the partner's products can be enabled.
export type PartnerRelationship = {
  partnerId: string;
  status: 'none' | 'pending' | 'approved' | 'suspended' | string;
  approvedAt: string | null;
  consentAt: string | null;
};

function mapMarketplaceProduct(p: any): MarketplaceProduct {
  return {
    ...mapProduct(p),
    partnerId: p.partner_id ?? '',
    partnerName: p.partner_name ?? 'Partner',
    agreementStatus: p.agreement_status ?? null,
  };
}

// --- shared status → pill meta (credit lifecycle) ---
export type CreditPill = { label: string; variant: 'secondary' | 'warning' | 'info' | 'success' | 'danger' };

export function applicationPill(status: string): CreditPill {
  switch (status) {
    case 'eligibility_passed':
      return { label: 'Pending review', variant: 'secondary' };
    case 'eligibility_failed':
      return { label: 'Not eligible', variant: 'warning' };
    case 'approved':
      return { label: 'Approved', variant: 'warning' };
    case 'rejected':
      return { label: 'Declined', variant: 'danger' };
    default:
      // A status this build does not know about must still READ as English —
      // "in_arrears" leaking to the screen is what sent this here. Humanise the
      // key rather than printing it verbatim.
      return {
        label: status
          ? status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
          : 'Unknown',
        variant: 'secondary',
      };
  }
}

export function facilityPill(status: string): CreditPill {
  switch (status) {
    case 'pending_disbursement':
      return { label: 'Awaiting payout', variant: 'warning' };
    case 'active':
      return { label: 'Active', variant: 'info' };
    // Both of these existed in credit-service and were missing here, so they
    // fell through to the default and rendered the RAW key: a loan behind on
    // repayments showed as "in_arrears" in the list. Arrears is also the one
    // status a person needs to notice, so it is danger, not secondary.
    case 'in_arrears':
      return { label: 'In arrears', variant: 'danger' };
    case 'disbursing':
      return { label: 'Paying out', variant: 'warning' };
    case 'settling':
      return { label: 'Settling', variant: 'info' };
    case 'paid_off':
      return { label: 'Paid off', variant: 'success' };
    case 'in_recovery':
      return { label: 'In recovery', variant: 'warning' };
    case 'defaulted':
      return { label: 'Defaulted', variant: 'danger' };
    case 'cancelled':
      return { label: 'Cancelled', variant: 'danger' };
    default:
      // A status this build does not know about must still READ as English —
      // "in_arrears" leaking to the screen is what sent this here.
      return {
        label: status
          ? status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
          : 'Unknown',
        variant: 'secondary',
      };
  }
}
